<template>
  <v-card dark flat class="video-migration">
    <div class="migration-heading">
      <div>
        <h2>Website-Videos</h2>
        <p>Übertrage bestehende Header-Videos einzeln nach Cloudflare. Öffne die App auf deinem Mac, um die Übertragung über deinen Mac auszuführen.</p>
        <p>Während der Übertragung diese Ansicht geöffnet lassen. Gemeinsam verwendete Videos erscheinen nur einmal.</p>
      </div>
      <v-btn text color="#58d388" :disabled="busy || loading" class="migration-button" @click="loadVideos">Aktualisieren</v-btn>
    </div>
    <v-progress-linear v-if="loading" indeterminate color="#58d388" aria-label="Videostatus laden" />
    <p v-if="loadError" role="alert" class="migration-error">{{ loadError }}</p>
    <p v-else-if="loaded && !videos.length">Keine Website-Videos vorhanden.</p>
    <div class="migration-list">
      <div v-for="video in videos" :key="video.file" class="migration-row">
        <div class="migration-copy">
          <h3>{{ video.locations.join(' · ') }}</h3>
          <span class="migration-file">{{ video.file }}</span>
          <span v-if="video.size" class="migration-size">{{ (video.size / 1000000).toFixed(1) }} MB</span>
          <p v-if="video.error" role="alert" class="migration-error">{{ video.error }}</p>
          <div v-if="activeFile === video.file" class="migration-progress" aria-live="polite">
            <p>{{ phaseLabel }}<span v-if="progress !== null"> · {{ progress }} %</span></p>
            <v-progress-linear :value="progress || 0" :indeterminate="progress === null" color="#58d388" :aria-label="phaseLabel" />
          </div>
        </div>
        <div class="migration-actions">
          <span v-if="video.state === 'ready'" class="migration-badge ready"><v-icon small color="#58d388">mdi-check-circle-outline</v-icon> Auf Cloudflare</span>
          <template v-else>
            <span class="migration-badge">{{ video.state === 'blocked' ? 'Prüfung nötig' : 'Noch lokal' }}</span>
            <v-btn v-if="activeFile === video.file" text class="migration-button" @click="cancel">Abbrechen</v-btn>
            <v-btn v-else-if="video.state !== 'blocked'" color="primary" :disabled="busy || loading" class="migration-button" @click="migrate(video)">
              {{ video.error ? 'Erneut versuchen' : 'Migrieren' }}
            </v-btn>
          </template>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import api from "@/api/websiteApi"
import { runWebsiteVideoMigration } from "@/services/websiteVideoMigrationService"

const PHASES = { prepare: "Migration vorbereiten", download: "Video auf dieses Gerät laden", check: "Video prüfen", upload: "Nach Cloudflare hochladen", verify: "Cloudflare bereitet das Video vor" }

export default {
  props: { active: Boolean, refreshKey: Number },
  data() {
    return { videos: [], loaded: false, loading: false, loadError: "", activeFile: "", phase: "", progress: null, job: null, controller: null, listController: null }
  },
  computed: {
    busy() { return !!this.activeFile },
    phaseLabel() { return PHASES[this.phase] || "Migration läuft" },
  },
  watch: {
    active: { immediate: true, handler(value) { if (value && !this.loaded) this.loadVideos() } },
    refreshKey() { if (this.active && !this.busy) this.loadVideos() },
  },
  mounted() { window.addEventListener("beforeunload", this.beforeUnload) },
  beforeDestroy() {
    this.cancel()
    if (this.listController) this.listController.abort()
    window.removeEventListener("beforeunload", this.beforeUnload)
    this.job = null
  },
  methods: {
    beforeUnload(event) {
      if (this.busy) { event.preventDefault(); event.returnValue = "" }
    },
    async loadVideos() {
      if (this.busy) return
      if (this.listController) this.listController.abort()
      const controller = new AbortController()
      this.listController = controller
      this.loading = true
      this.loadError = ""
      try {
        const result = await api.fetchWebsiteVideoMigrations(controller.signal)
        if (controller.signal.aborted) return
        this.videos = result.videos.map((video) => ({ ...video, error: video.error || "" }))
        this.loaded = true
      } catch (err) {
        if (!controller.signal.aborted) this.loadError = err.response?.data?.error || "Die Website-Videos konnten nicht geladen werden. Bitte erneut aktualisieren."
      } finally {
        if (this.listController === controller) { this.loading = false; this.listController = null }
      }
    },
    async migrate(video) {
      if (this.busy || this.loading || video.state !== "pending") return
      const controller = new AbortController()
      this.controller = controller
      this.activeFile = video.file
      video.error = ""
      if (this.job?.file !== video.file) this.job = { file: video.file, blob: null, sourceVersion: null }
      try {
        await runWebsiteVideoMigration(this.job, { signal: controller.signal, onProgress: (phase, value) => { this.phase = phase; this.progress = value } })
        if (!controller.signal.aborted) { video.state = "ready"; this.job = null }
      } catch (err) {
        video.error = controller.signal.aborted ? "Migration unterbrochen. Du kannst sie erneut starten." : err.response?.data?.error || err.message || "Migration fehlgeschlagen. Bitte erneut versuchen."
      } finally {
        if (this.controller === controller) { this.controller = null; this.activeFile = ""; this.phase = ""; this.progress = null }
      }
    },
    cancel() { if (this.controller) this.controller.abort() },
  },
}
</script>

<style scoped>
.video-migration { margin-bottom: 1rem; padding: 1.375rem; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 1.375rem; background: rgba(255, 255, 255, 0.03); font-size: 1rem; }
.migration-heading, .migration-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
.migration-heading { margin-bottom: 1rem; }
.migration-heading h2 { margin: 0; font-size: 1.25rem; }
.video-migration p { margin: 0.375rem 0; font-size: 0.875rem; line-height: 1.5; color: #b8b8bc; }
.migration-row { padding: 1rem 0; border-top: 1px solid rgba(255, 255, 255, 0.1); }
.migration-copy { flex: 1; min-width: 0; }
.migration-copy h3 { margin: 0 0 0.375rem; font-size: 1rem; line-height: 1.5; overflow-wrap: anywhere; }
.migration-file { font-size: 0.75rem; color: #b8b8bc; overflow-wrap: anywhere; }
.migration-size { margin-left: 0.75rem; font-size: 0.875rem; color: #b8b8bc; }
.migration-actions { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 0.75rem; }
.migration-badge { display: inline-flex; align-items: center; gap: 0.375rem; font-size: 0.75rem; color: #b8b8bc; }
.migration-badge.ready { color: #58d388; }
.migration-button { font-size: 0.875rem; text-transform: none; flex-shrink: 0; }
.video-migration .migration-error { color: #ffb4b4; }
.migration-progress { margin-top: 0.75rem; }
@media (max-width: 47.9375rem) {
  .video-migration { padding: 1rem; }
  .migration-heading, .migration-row { flex-direction: column; }
  .migration-copy, .migration-actions { width: 100%; }
  .migration-actions { justify-content: flex-start; }
}
</style>
